import { action, observable, makeObservable } from 'mobx';

import { t } from '@gem/modules/i18n';

import {
  IAlbumMessagePayload,
  IMessageKeyWord,
  IMessagePayload,
  IOrderedItemMessagePayload,
  IUserMention,
  PayloadType,
} from 'APP/model/message/messageModel.types';
import { MessageError } from 'STORE/Messages/Message/ChatMessage/ChatMessages.types';

import { ImagePayload } from '../ImagePayload/ImagePayload';
import { Payload } from '../Payload';
import { VideoPayload } from '../VideoPayload/VideoPayload';

const PayloadClass = {
  [PayloadType.Image]: ImagePayload,
  [PayloadType.Video]: VideoPayload,
};

interface IAlbumOrderedItemMessagePayload extends IOrderedItemMessagePayload {
  payload: IMessagePayload & {
    userFile: File | Blob;
    payloadType: PayloadType.Image | PayloadType.Video;
  };
}

interface IAlbumPayloadParams {
  payloads: IOrderedItemMessagePayload[];
  comment: string | null;
  keyWord?: IMessageKeyWord;
  userMentions?: IUserMention[];
}

interface IAlbumPayloadPayloadsItem {
  id: string;
  order: number;
  payloadType: PayloadType;
  payload: ImagePayload | VideoPayload;
}

export class AlbumPayload extends Payload {
  @observable payloads: IAlbumPayloadPayloadsItem[] = [];
  @observable comment: string | null = null;
  keyWord: IMessageKeyWord | null = null;
  keyWords: IMessageKeyWord[] = [];
  userMentions: IUserMention[] = [];

  constructor(data: IAlbumPayloadParams) {
    super({ ...data, payloadType: PayloadType.Album });

    makeObservable(this);

    this.payloads = data.payloads
      .filter((payload): payload is IAlbumOrderedItemMessagePayload => {
        return [PayloadType.Image, PayloadType.Video].includes(payload.payload.payloadType);
      })
      .sort((a, b) => a.order - b.order)
      .map(({ id, order, payloadType, payload }) => ({
        id,
        order,
        payloadType,
        payload: new PayloadClass[payload.payloadType]({
          ...payload,
          message: {
            delete: (): Promise<boolean> => this.delete(payload.userFile),
            forward: null,
            repliedMessage: null,
            quote: null,
            hasError: false,
            setError(): void {
              this.hasError = true;
            },
          },
        }),
      }));

    this.comment = data.comment;
    this.keyWord = data.keyWord || null;
    this.keyWords = data.keyWord ? [data.keyWord] : [];
    this.userMentions = data.userMentions || [];
  }

  toJSON(): IAlbumMessagePayload {
    return {
      ...super.toJSON(),
      comment: this.comment || undefined,
      userMentions: this.userMentions || null,
      keyWord: this.keyWord || undefined,

      payloads: this.payloads.map((payload, idx) => ({
        id: payload.id,
        payloadType: payload.payloadType,
        order: idx,
        payload: payload.payload.toJSON(),
      })),
    };
  }

  get text(): string {
    return t('message_desctription_album');
  }

  get typeDescription(): string {
    return t('message_desctription_album');
  }

  @action
  async delete(file: File | Blob): Promise<boolean> {
    const filteredPayloads = this.payloads.filter(({ payload }) => payload.userFile !== file);
    const result = await (filteredPayloads.length === 0
      ? this.message?.delete() || Promise.resolve(false)
      : Promise.resolve(true));

    if (result) {
      this.payloads = filteredPayloads;
    }

    if (this.message?.canBeResend) {
      this.message.resend();
    }

    return result;
  }

  @action
  async upload(): Promise<void> {
    await Promise.allSettled(this.payloads.map(({ payload }) => payload.upload()));
    const hasError =
      this.payloads.length === 0 || this.payloads.some(({ payload }) => !payload.isLoaded);
    if (hasError) {
      this.message?.setError(MessageError.Send);
    }
  }
}
